import { useEffect, useState } from 'react';
import type { TRPC } from '../../contexts/TRPC';
import { isLeftResult, isRightResult } from '../../lib/trpcTypeGuards';
import { captureAndReportError } from '../../utils/errorReporting';
import type { AdminReportingAppDetailOutput } from './types';

interface UseReportingAppDetailOptions {
  trpc: TRPC;
  androidPackage: string | null;
  enabled: boolean;
}

export function useReportingAppDetail({
  trpc,
  androidPackage,
  enabled,
}: UseReportingAppDetailOptions) {
  const [data, setData] = useState<AdminReportingAppDetailOutput | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isCancelled = false;

    const load = async () => {
      if (!enabled || !androidPackage) {
        setData(null);
        setError(null);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setData(null);
      setError(null);

      try {
        const result = await trpc.client.getAdminReportingAppDetail.query({
          androidPackage,
        });

        if (isCancelled) {
          return;
        }

        if (isRightResult(result)) {
          setData(result.right);
          return;
        }

        if (isLeftResult(result)) {
          setData(null);
          setError(result.left.message);
        }
      } catch (loadError) {
        if (!isCancelled) {
          captureAndReportError(loadError, {
            operation: 'reporting.appDetail.load',
            component: 'useReportingAppDetail',
            androidPackage,
          });
          setData(null);
          setError('Failed to load app store detail');
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      isCancelled = true;
    };
  }, [androidPackage, enabled, trpc]);

  return {
    data,
    error,
    isLoading,
  };
}
